import {useCallback, useMemo, useState} from 'react'
import {View} from 'react-native'
import {useAnimatedRef} from 'react-native-reanimated'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {type NativeStackScreenProps} from '@react-navigation/native-stack'

import {useInitialNumToRender} from '#/lib/hooks/useInitialNumToRender'
import {type StudioTabNavigatorParams} from '#/lib/routes/types'
import {useNavigation} from '@react-navigation/native'
import {useSession} from '#/state/session'
import {useProfileQuery} from '#/state/queries/profile'
import {useSimpleVerificationState} from '#/components/verification'
import {useVerifiedUserQuery} from '#/state/queries/verified'
import {cleanError} from '#/lib/strings/errors'
import {
  useStudioVideosQuery,
  useStudioAnalyticsQuery,
} from '#/state/queries/studio'
import {List, type ListRef} from '#/view/com/util/List'
import {atoms as a, useBreakpoints, useTheme} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {type DialogControlProps, useDialogControl} from '#/components/Dialog'
import {ArrowRotateCounterClockwise_Stroke2_Corner0_Rounded as RetryIcon} from '#/components/icons/ArrowRotate'
import {PlusLarge_Stroke2_Corner0_Rounded as PlusIcon} from '#/components/icons/Plus'
import {VideoClip_Stroke2_Corner0_Rounded as VideoIcon} from '#/components/icons/VideoClip'
import * as Layout from '#/components/Layout'
import {Link} from '#/components/Link'
import {Text} from '#/components/Typography'
import {IS_NATIVE} from '#/env'
import type {StudioVideo} from '#/lib/api/studio'
import {StudioUploadDialog} from './StudioUploadDialog'
import {StudioVideoEditDialog} from './StudioVideoEditDialog'
import {StudioVideoListItem} from './StudioVideoListItem'

type Props = NativeStackScreenProps<StudioTabNavigatorParams, 'Studio'>

function AnalyticsChart({
  data,
}: {
  data: {view_date: string; views_count: number}[]
}) {
  const t = useTheme()
  const maxViews = useMemo(
    () => Math.max(1, ...data.map(d => d.views_count)),
    [data],
  )
  const displayData = useMemo(
    () => data.slice(-14).reverse(),
    [data],
  )

  if (displayData.length === 0) {
    return (
      <View style={[a.p_md, a.rounded_md, t.atoms.bg_contrast_25]}>
        <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
          <Trans>No view data yet</Trans>
        </Text>
      </View>
    )
  }

  return (
    <View style={[a.p_md, a.rounded_md, t.atoms.bg_contrast_25]}>
      <Text style={[a.text_sm, a.font_semi_bold, a.pb_sm, t.atoms.text]}>
        <Trans>Views (last 14 days)</Trans>
      </Text>
      <View style={[a.flex_row, a.align_end, a.gap_2xs, {minHeight: 80}]}>
        {displayData.map((day, i) => (
          <View
            key={day.view_date}
            style={[
              a.flex_1,
              a.rounded_xs,
              {
                height: `${Math.max(4, (day.views_count / maxViews) * 100)}%`,
                backgroundColor: t.palette.primary_500,
              },
            ]}
          />
        ))}
      </View>
      <View style={[a.flex_row, a.justify_between, a.pt_xs]}>
        <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>
          {displayData[0]?.view_date}
        </Text>
        <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>
          {displayData[displayData.length - 1]?.view_date}
        </Text>
      </View>
    </View>
  )
}

function keyExtractorVideo(item: StudioVideo) {
  return item.id
}

export function StudioScreen({navigation}: Props) {
  const {_} = useLingui()
  const t = useTheme()
  const {currentAccount} = useSession()
  const {data: profile} = useProfileQuery({did: currentAccount?.did})
  const {isVerified: isBlueskyVerified} = useSimpleVerificationState({
    profile: profile ?? undefined,
  })
  const {data: verifiedUser} = useVerifiedUserQuery(currentAccount?.did)
  const isVerified = isBlueskyVerified || Boolean(verifiedUser)
  const {gtMobile} = useBreakpoints()
  const uploadControl = useDialogControl()
  const editControl = useDialogControl()
  const [videoToEdit, setVideoToEdit] = useState<StudioVideo | null>(null)
  const scrollRef: ListRef = useAnimatedRef()
  const [isPTRing, setIsPTRing] = useState(false)

  const {
    data: videosRaw = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useStudioVideosQuery({ownerDid: currentAccount?.did ?? null})

  const videos = useMemo(() => {
    const did = currentAccount?.did
    if (!did) return []
    return videosRaw.filter(v => v.owner_did === did)
  }, [videosRaw, currentAccount?.did])

  const {data: analytics = []} = useStudioAnalyticsQuery()

  const initialNumToRender = useInitialNumToRender({minItemHeight: 100})

  const onRefresh = useCallback(async () => {
    setIsPTRing(true)
    try {
      await refetch()
    } finally {
      setIsPTRing(false)
    }
  }, [refetch])

  const openUpload = useCallback(() => {
    uploadControl.open()
  }, [uploadControl])

  const openEdit = useCallback(
    (video: StudioVideo) => {
      setVideoToEdit(video)
      editControl.open()
    },
    [editControl],
  )

  const renderVideoItem = useCallback(
    ({item}: {item: StudioVideo}) => (
      <StudioVideoListItem video={item} onEdit={openEdit} />
    ),
    [openEdit],
  )

  if (videos.length === 0 && !isLoading) {
    return (
      <Layout.Screen>
        <StudioHeader
          uploadControl={uploadControl}
          uploadDisabled={!isVerified}
        />
        <Layout.Center>
          {isError ? (
            <View style={[a.pt_3xl, a.align_center]}>
              <VideoIcon width={48} fill={t.atoms.text_contrast_low.color} />
              <Text
                style={[a.pt_md, a.pb_sm, a.text_2xl, a.font_semi_bold, t.atoms.text]}>
                <Trans>Whoops!</Trans>
              </Text>
              <Text
                style={[
                  a.text_md,
                  a.pb_xl,
                  a.text_center,
                  t.atoms.text_contrast_medium,
                  {maxWidth: 360},
                ]}>
                {cleanError(error) || _(msg`Failed to load videos`)}
              </Text>
              <Button
                label={_(msg`Retry`)}
                size="small"
                color="secondary_inverted"
                variant="solid"
                onPress={() => refetch()}>
                <ButtonText>
                  <Trans>Retry</Trans>
                </ButtonText>
                <ButtonIcon icon={RetryIcon} position="right" />
              </Button>
            </View>
          ) : (
            <View style={[a.pt_3xl, a.align_center]}>
              <VideoIcon width={48} fill={t.palette.primary_500} />
              <Text
                style={[a.pt_md, a.pb_sm, a.text_2xl, a.font_semi_bold, t.atoms.text]}>
                <Trans>My Videos</Trans>
              </Text>
              <Text
                style={[
                  a.text_md,
                  a.pb_xl,
                  a.text_center,
                  t.atoms.text_contrast_medium,
                ]}>
                <Trans>Upload your first video to get started.</Trans>
              </Text>
              <Button
                label={_(msg`Upload video`)}
                color="primary"
                size="medium"
                onPress={openUpload}
                disabled={!isVerified}>
                <ButtonIcon icon={PlusIcon} position="left" />
                <ButtonText>
                  <Trans>Upload video</Trans>
                </ButtonText>
              </Button>
            </View>
          )}
        </Layout.Center>
        <StudioUploadDialog control={uploadControl} onSuccess={refetch} />
        <StudioVideoEditDialog
          control={editControl}
          video={videoToEdit}
          onSuccess={refetch}
        />
      </Layout.Screen>
    )
  }

  return (
    <Layout.Screen testID="studioScreen">
      <StudioHeader
        uploadControl={uploadControl}
        uploadDisabled={!isVerified}
      />
      <List
        ref={scrollRef}
        data={videos}
        renderItem={renderVideoItem as (info: {item: StudioVideo}) => React.ReactElement}
        keyExtractor={keyExtractorVideo}
        refreshing={isPTRing}
        onRefresh={onRefresh}
        ListHeaderComponent={
          <View style={[a.px_lg, a.pb_md]}>
            <AnalyticsChart data={analytics} />
            <Link
              label={_(msg`Watch your published videos`)}
              to="/studio/feed"
              style={[a.pt_md, a.pb_sm]}>
              <Text style={[a.text_sm, {color: t.palette.primary_500}]}>
                <Trans>Watch your published videos</Trans>
              </Text>
            </Link>
          </View>
        }
        onEndReachedThreshold={IS_NATIVE ? 1.5 : 0}
        initialNumToRender={initialNumToRender}
        windowSize={11}
        desktopFixedHeight
        sideBorders={false}
      />
      <StudioUploadDialog control={uploadControl} onSuccess={refetch} />
      <StudioVideoEditDialog
        control={editControl}
        video={videoToEdit}
        onSuccess={refetch}
      />
    </Layout.Screen>
  )
}

function StudioHeader({
  uploadControl,
  uploadDisabled = false,
}: {
  uploadControl: DialogControlProps
  uploadDisabled?: boolean
}) {
  const {_} = useLingui()
  const {gtMobile} = useBreakpoints()
  const navigation = useNavigation()

  const openUpload = useCallback(() => {
    uploadControl.open()
  }, [uploadControl])

  const openFeed = useCallback(() => {
    navigation.navigate('StudioFeed')
  }, [navigation])

  return (
    <Layout.Header.Outer>
      {gtMobile ? (
        <>
          <Layout.Header.Content>
            <Layout.Header.TitleText>
              <Trans>My Videos</Trans>
            </Layout.Header.TitleText>
          </Layout.Header.Content>
          <View style={[a.flex_row, a.align_center, a.gap_sm]}>
            <Button
              label={_(msg`Watch`)}
              color="secondary"
              size="small"
              onPress={openFeed}>
              <ButtonText>
                <Trans>Watch</Trans>
              </ButtonText>
            </Button>
            <Button
              label={_(msg`Upload`)}
              color="primary"
              size="small"
              variant="solid"
              onPress={openUpload}
              disabled={uploadDisabled}>
              <ButtonIcon icon={PlusIcon} position="left" />
              <ButtonText>
                <Trans>Upload</Trans>
              </ButtonText>
            </Button>
          </View>
        </>
      ) : (
        <>
          <Layout.Header.MenuButton />
          <Layout.Header.Content>
            <Layout.Header.TitleText>
              <Trans>My Videos</Trans>
            </Layout.Header.TitleText>
          </Layout.Header.Content>
          <Layout.Header.Slot>
            <Button
              label={_(msg`Watch`)}
              color="secondary"
              size="small"
              onPress={openFeed}>
              <ButtonText>
                <Trans>Watch</Trans>
              </ButtonText>
            </Button>
            <Button
              label={_(msg`Upload`)}
              color="primary"
              size="small"
              onPress={openUpload}
              disabled={uploadDisabled}>
              <ButtonIcon icon={PlusIcon} position="left" />
              <ButtonText>
                <Trans>Upload</Trans>
              </ButtonText>
            </Button>
          </Layout.Header.Slot>
        </>
      )}
    </Layout.Header.Outer>
  )
}
